<template>
    <DCard padding>
        <DRow align-vertical="end">
            <DCol
                sm="12"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    class="float-end"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="4"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.totalPostingAmount"
                    :label="__('tools.customer_balances.total_posting_amount')"
                    :options="postingAmountOptions"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2 pt-2"
            >
                <DButton
                    :label="__('messages.actions.filter')"
                    @click="changePage(customers.current_page)"
                />
                <DButton
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            class="table-responsive"
            :items="customers.data"
            :fields="getFields"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            @update:sorter-value="updateOrderValue"
        >
            <template #customer_id="{item}">
                <td>
                    <Link :href="customerProfileRoute(item)">
                        {{ item.customer_id }}
                    </Link>
                </td>
            </template>
            <template #total_posting_amount="{item}">
                <td>
                    {{ priceFormatForDisplay(item.total_posting_amount, 2, item.market_region_id) }}
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="customers.last_page > 1"
            :active-page="customers.current_page"
            :pages="customers.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'

const props = defineProps({
    customers: {
        type: Object,
        default: () => {
            return {}
        }
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Array,
        default: () => {
            return []
        }
    },
    filterOptions: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const filterData = {
    totalPostingAmount: props.filters.totalPostingAmount ?? null,
    orderBy: props.filters.orderBy ?? 'customer_id',
    orderByAsc: getSortDirection(),
    saveFilters: 0,
    defaultFilters: 0
}

const postingAmountOptions = computed(function () {
    return props.filterOptions.totalPostingAmount.map(status => {
        return {
            value: status.value,
            label: status.label
        }
    })
})

const showListViewModal = ref(false)

const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: __('settings.list_view.' + item.list + '.' + item.column),
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    return availableList
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const sorterValue = computed(() => {
    return {
        column: filterData.orderBy,
        asc: !!filterData.orderByAsc
    }
})

const updateOrderValue = (event) => {
    filterData.orderBy = event.column
    filterData.orderByAsc = event.asc ? 1 : 0

    changePage(props.customers.current_page)
}

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData).filter(([_, v]) => v))

    if (page > 1) {
        query.page = page
    }

    router.get(route('tools.customer-balances.index', {
        saveFilters: true
    }), query)
}

const resetFilters = () => {
    router.get(route('tools.customer-balances.index', {
        saveFilters: 1,
        defaultFilters: 1
    }))
}

function getSortDirection () {
    if (props.filters.orderByAsc === null) {
        return null
    }

    return props.filters.orderByAsc ? 1 : 0
}

function customerProfileRoute (item) {
    return route('customers.customers.show', { customer: item.customer_id })
}

</script>
